/**
 * Recovery for lazy-chunk load failures after a frontend redeploy.
 *
 * Why this exists: every build hashes its lazy chunks (`outputHashing: all`).
 * A browser tab (or disk cache) that loaded `index.html` BEFORE a deploy still
 * knows only the OLD hashes. When the user navigates to a lazy route, it
 * requests an old `*.js` that no longer exists; Firebase Hosting's catch-all
 * rewrite (`** -> /index.html`) answers with `index.html` (MIME `text/html`),
 * so the dynamic import rejects with a `ChunkLoadError` / "Failed to load
 * module script". This is independent of the service worker (which only runs in
 * the production build) — it happens in dev too.
 *
 * The fix: when we see such an error, force a single full reload so the tab
 * pulls the fresh `index.html` (served `no-cache`) and the new hashes. A guard
 * in `sessionStorage` prevents a reload loop if the chunk is genuinely missing
 * even after a fresh index (in which case we let the error surface normally).
 */

/** Min delay between two recovery reloads, in ms. Below this we assume a loop. */
export const CHUNK_RELOAD_COOLDOWN_MS = 15_000;

/** sessionStorage key holding the timestamp (ms) of the last recovery reload. */
export const CHUNK_RELOAD_KEY = '__proporioChunkReloadAt';

/**
 * Recognise the various shapes a failed lazy-chunk load takes across browsers
 * and bundlers (webpack jsonp loader, native ESM dynamic import).
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  // Unwrap Angular's async-rejection envelope: `{ rejection: <error> }`.
  if (typeof error === 'object' && error !== null && 'rejection' in error) {
    if (isChunkLoadError((error as { rejection: unknown }).rejection)) return true;
  }

  const name = (error as { name?: unknown }).name;
  if (typeof name === 'string' && name === 'ChunkLoadError') return true;

  const message = (error as { message?: unknown }).message;
  const text = typeof message === 'string' ? message : String(error ?? '');
  return (
    /Loading chunk \S+ failed/i.test(text) ||
    /ChunkLoadError/i.test(text) ||
    /Failed to load module script/i.test(text) ||
    /error loading dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text) ||
    /'?text\/html'?/i.test(text) && /module script/i.test(text)
  );
}

/**
 * In-memory fallback for environments where `sessionStorage` is blocked
 * (privacy mode, sandboxed iframe). Persists for the life of the document so
 * the cooldown still guards against a reload loop even without storage.
 */
let lastReloadFallback = 0;

/** Reset the in-memory fallback. Test-only seam. */
export function resetChunkReloadFallback(): void {
  lastReloadFallback = 0;
}

/**
 * Attempt to recover from a chunk-load error by reloading once. Returns `true`
 * if a reload was triggered (caller should then swallow the error), `false` if
 * we are within the cooldown (caller should let the error surface).
 *
 * `win` and `now` are injectable for testing.
 */
export function tryRecoverFromChunkError(
  win: Window = window,
  now: number = Date.now()
): boolean {
  let stored: number;
  try {
    const raw = win.sessionStorage?.getItem(CHUNK_RELOAD_KEY);
    stored = raw ? Number(raw) : 0;
  } catch {
    // sessionStorage can throw (privacy mode, blocked storage) — rely on the
    // in-memory fallback below instead.
    stored = 0;
  }
  const last = Math.max(Number.isFinite(stored) ? stored : 0, lastReloadFallback);

  if (now - last < CHUNK_RELOAD_COOLDOWN_MS) {
    // We already reloaded very recently — a fresh index still can't find the
    // chunk. Stop looping; let the error surface so it is visible/reportable.
    return false;
  }

  lastReloadFallback = now;
  try {
    win.sessionStorage?.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    // Ignore storage write failures; the in-memory fallback still guards.
  }

  win.location.reload();
  return true;
}
